import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Box, Spinner, Text, VStack, useToast } from '@chakra-ui/react';
import { HTTP } from 'utils/http';
import * as Sentry from '@sentry/react';

export function ScaCallback() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const toast = useToast();
  const [isProcessing, setIsProcessing] = useState(true);
  const [status, setStatus] = useState<'processing' | 'success' | 'error'>('processing');

  useEffect(() => {
    const processScaAuthentication = async () => {
      try {
        // ÉTAPE 1: Récupérer les paramètres de retour Stripe/Mindbody
        // Les paramètres peuvent être dans la query string (avant #) ou après le hash
        const urlParams = new URLSearchParams(window.location.search);
        const hashParams = searchParams;

        // Priorité aux paramètres de query string puis searchParams
        const paymentIntent = urlParams.get('payment_intent') || hashParams.get('payment_intent') || null;
        const paymentIntentClientSecret =
          urlParams.get('payment_intent_client_secret') || hashParams.get('payment_intent_client_secret') || null;
        const sourceType = urlParams.get('source_type') || hashParams.get('source_type') || null;
        const redirectStatus = urlParams.get('redirect_status') || hashParams.get('redirect_status') || null;

        // Compiler tous les paramètres pour debugging
        const allParams = {
          // Query string params
          queryParams: Object.fromEntries(urlParams.entries()),
          // Hash params
          hashParams: Object.fromEntries(hashParams.entries()),
          // URL complète
          fullUrl: window.location.href,
        };

        console.log('SCA Callback - Stripe/Mindbody return parameters detected', {
          paymentIntent: paymentIntent || 'missing',
          paymentIntentClientSecret: paymentIntentClientSecret || 'missing',
          sourceType,
          redirectStatus,
          allParams,
        });

        // ÉTAPE 2: Récupérer les données de paiement sauvegardées
        const savedPaymentData = sessionStorage.getItem('scaPaymentData');
        if (!savedPaymentData) {
          throw new Error('Aucune donnée de paiement trouvée en session');
        }

        const paymentData = JSON.parse(savedPaymentData);
        console.log('SCA Callback - Payment data retrieved from session', {
          hasServiceId: !!paymentData.ServiceId,
          hasTransactionIDs: !!paymentData.TransactionIDs,
          transactionIDs: paymentData.TransactionIDs,
          paymentAmount: paymentData.Amount,
          originalResponse: paymentData.originalResponse ? 'present' : 'missing',
        });

        // ÉTAPE 3: Vérifications
        if (!paymentData.ServiceId) {
          throw new Error('ServiceId manquant dans les données de session');
        }

        if (
          !paymentData.TransactionIDs ||
          !Array.isArray(paymentData.TransactionIDs) ||
          paymentData.TransactionIDs.length === 0
        ) {
          throw new Error('TransactionIDs manquants ou invalides dans les données de session');
        }

        // ÉTAPE 4: Vérifier le statut de l'authentification
        // Pour Stripe, on vérifie la présence des paramètres de succès
        const authenticationSuccessful = !!(paymentIntent && paymentIntentClientSecret);

        console.log('SCA Callback - Authentication status check', {
          authenticationSuccessful,
          hasRequiredStripeParams: !!(paymentIntent && paymentIntentClientSecret),
          redirectStatus,
          sourceType,
        });

        if (!authenticationSuccessful) {
          // Si l'authentification a échoué ou est incomplète
          console.error('SCA Callback - Authentication failed or incomplete', {
            paymentIntent: !!paymentIntent,
            paymentIntentClientSecret: !!paymentIntentClientSecret,
            redirectStatus,
            allParams,
          });

          throw new Error(`Authentification 3D Secure échouée ou incomplète. Status: ${redirectStatus || 'unknown'}`);
        }

        setStatus('processing');
        console.log('SCA Callback - Making second API call to complete SCA purchase');

        // ÉTAPE 5: Préparer les données pour le second appel
        // On doit passer TOUTES les données originales + TransactionIDs selon la doc SCA
        // Extraire les données de paiement sans les métadonnées de transaction
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const { ServiceId: _, TransactionIDs: __, originalResponse: ___, ...paymentOnlyData } = paymentData;

        const completionData = {
          ServiceId: paymentData.ServiceId,
          TransactionIDs: paymentData.TransactionIDs,
          Payment: {
            // Inclure seulement les données de paiement, pas les métadonnées de transaction
            ...paymentOnlyData,
          },
        };

        console.log('SCA Callback - Completion data prepared', completionData);

        // ÉTAPE 6: Effectuer le second appel API
        const response = await HTTP.post('/sale/scacomplete', completionData).send();

        console.log('SCA Callback - Purchase completion successful', {
          response,
          responseKeys: Object.keys(response || {}),
          hasShoppingCart: !!(response && typeof response === 'object' && 'ShoppingCart' in response),
        });

        // ÉTAPE 7: Nettoyer la session et rediriger
        sessionStorage.removeItem('scaPaymentData');
        setStatus('success');

        toast({
          status: 'success',
          title: 'Paiement réussi !',
          description: 'Votre achat a été confirmé avec succès.',
          duration: 5000,
          isClosable: true,
        });

        // Redirection après succès
        setTimeout(() => {
          navigate('/account/services', { replace: true });
        }, 2000);
      } catch (error) {
        console.error('SCA authentication error:', error);

        const errorMessage = error instanceof Error ? error.message : 'Erreur inconnue';
        console.log('SCA Callback - Error details', {
          error: errorMessage,
          paymentDataExists: !!sessionStorage.getItem('scaPaymentData'),
          urlParams: new URLSearchParams(window.location.search).toString(),
          hashParams: searchParams.toString(),
          fullUrl: window.location.href,
        });

        Sentry.captureException(error);
        setStatus('error');

        toast({
          status: 'error',
          title: "Erreur d'authentification",
          description: errorMessage,
          duration: 8000,
          isClosable: true,
        });

        // En cas d'erreur, rediriger vers la page de checkout après un délai
        setTimeout(() => {
          sessionStorage.removeItem('scaPaymentData');
          navigate('/', { replace: true });
        }, 5000);
      } finally {
        setIsProcessing(false);
      }
    };

    // Démarrer le processus après un court délai pour laisser la page se charger
    const timeoutId = setTimeout(processScaAuthentication, 500);
    return () => clearTimeout(timeoutId);
  }, [searchParams, navigate, toast]);

  const getMessage = () => {
    switch (status) {
      case 'processing':
        return 'Finalisation de votre authentification 3D Secure...';
      case 'success':
        return 'Authentification réussie ! Redirection en cours...';
      case 'error':
        return "Échec de l'authentification. Redirection vers le checkout...";
      default:
        return '';
    }
  };

  const getColor = () => {
    switch (status) {
      case 'processing':
        return 'blue.500';
      case 'success':
        return 'green.500';
      case 'error':
        return 'red.500';
      default:
        return 'gray.500';
    }
  };

  return (
    <Box minH="100vh" display="flex" alignItems="center" justifyContent="center" bg="gray.50">
      <VStack spacing={6} textAlign="center" p={8} bg="white" borderRadius="lg" boxShadow="lg">
        {isProcessing ? (
          <Spinner size="xl" color="blue.500" thickness="4px" />
        ) : status === 'success' ? (
          <Box fontSize="4xl" color="green.500">
            ✓
          </Box>
        ) : (
          <Box fontSize="4xl" color="red.500">
            ✗
          </Box>
        )}

        <Text fontSize="xl" fontWeight="semibold" color={getColor()}>
          {getMessage()}
        </Text>

        {status === 'processing' && (
          <Text fontSize="sm" color="gray.600">
            Merci de ne pas fermer cette page pendant le traitement.
          </Text>
        )}

        {status === 'error' && (
          <Text fontSize="sm" color="gray.600">
            Si le problème persiste, veuillez contacter notre support client.
          </Text>
        )}
      </VStack>
    </Box>
  );
}
